import { useRef, useState, useEffect } from 'react';
import { Phone, Settings, GraduationCap, Rocket } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';

function useReveal(ref) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setRevealed(true); }, { threshold: 0.1 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return revealed;
}

export default function HowItWorksSection() {
  const ref = useRef(null);
  const revealed = useReveal(ref);
  const { t } = useLanguage();

  const steps = [
    { icon: Phone, title: t('how.s1Title'), duration: t('how.s1Duration'), description: t('how.s1Desc'), color: '#0095fc' },
    { icon: Settings, title: t('how.s2Title'), duration: t('how.s2Duration'), description: t('how.s2Desc'), color: '#7800f7' },
    { icon: GraduationCap, title: t('how.s3Title'), duration: t('how.s3Duration'), description: t('how.s3Desc'), color: '#0ff0b3' },
    { icon: Rocket, title: t('how.s4Title'), duration: t('how.s4Duration'), description: t('how.s4Desc'), color: '#0095fc' },
  ];

  return (
    <section id="como-funciona" ref={ref} className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-xs font-black tracking-widest uppercase text-brand-blue mb-4 px-3 py-1 bg-blue-50 rounded-full">{t('how.eyebrow')}</span>
          <h2 className="font-heading font-light text-4xl lg:text-5xl text-gray-900 leading-tight mb-4 tracking-tight">
            {t('how.title1')} <span className="text-brand-blue">{t('how.titleHighlight')}</span>
          </h2>
          <p className="text-gray-500 text-lg font-light max-w-2xl mx-auto">
            {t('how.desc')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-brand-blue via-brand-violet to-brand-blue opacity-20" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={i}
                className={`relative bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-500 hover-lift ${
                  revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: revealed ? '0ms' : `${200 + i * 120}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 relative z-10" style={{ background: `${step.color}15` }}>
                  <Icon size={26} style={{ color: step.color }} />
                </div>
                <span className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2 block">{step.duration}</span>
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed font-light">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}